import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { PanelLeftClose, PanelLeftOpen, Search } from 'lucide-react';
import { BackButton, IconButton } from './ui';
import { DevMenu } from './dev/DevMenu';
import { DEV_LONG_PRESS_MS, devToolsAvailable, devToolsUnlocked } from '../../lib/dev-tools';
import { requestDevToolsAccess, revalidateDevToolsAccess } from '../../lib/dev-tools-access';
import { SymphonyMark } from '../SymphonyMark';
import { useGenericBi } from '../../lib/use-generic-bi';
import { useBasePath } from '../../lib/use-base-path';
import { useUnreadNotifications } from '../../lib/use-unread-notifications';
import { useVoiceCallSummary } from '../../lib/voice-call-presence-context';
import { useTranslation } from '../../lib/i18n';

// Brand mark display size.
const MARK_SIZE = 36;

/**
 * Brand header — Symphony mark + wordmark anchored at top-left of the viewport.
 * In wide mode it's the rail collapse/expand toggle and shows the mark; in
 * narrow mode it's the drawer toggle and shows a two-line hamburger (matching
 * the native apps' menu button), with the logo returning while the drawer is open.
 *
 * On a drill-in screen (`showBack`) the leading slot instead shows a BACK button — the web side of
 * the native `topShowsMenu` rule (see `lib/top-shows-menu.ts`). `showBack` is only ever passed on
 * NARROW: mobile carries back in the header (replacing the hamburger), while desktop carries it in
 * the page content (the Breadcrumb slot) and keeps the brand-mark / rail-collapse toggle here.
 */
export function BrandHeader({
  narrow,
  sidebarOpen,
  onToggle,
  onToggleCollapsed,
  onOpenSearch,
  elevated = false,
  collapsed = false,
  largeMark = false,
  showBack = false,
  onBack,
  backPageName,
}: {
  narrow: boolean;
  sidebarOpen: boolean;
  onToggle: () => void;
  onToggleCollapsed?: () => void;
  // Open the search dialog — the header search icon (wide only; the collapsed
  // strip and the narrow drawer reach search elsewhere).
  onOpenSearch?: () => void;
  // Lifted above the full-screen chat (z50) so it stays tappable as the drawer
  // toggle from inside a maximized chat on narrow.
  elevated?: boolean;
  collapsed?: boolean;
  // Home/narrow uses the larger 57px sticky menu button (Figma 8265-86809);
  // every other narrow screen keeps the 40px toggle.
  largeMark?: boolean;
  // Drill-in screen: the leading slot is a BACK button instead of the menu/mark.
  showBack?: boolean;
  onBack?: () => void;
  // The current page's BI name, so the back click is attributable per-page (native's
  // `currentPageBiName` lesson) rather than to a single hardcoded name.
  backPageName?: string;
}) {
  const { logPageClickBi } = useGenericBi();
  const { t } = useTranslation();
  const unreadCount = useUnreadNotifications();
  const voiceCallSurfaceVisible = useVoiceCallSummary()?.timing.surfaceVisible ?? false;
  const menuVisible = !narrow || sidebarOpen;
  const showHamburger = !menuVisible;
  // Mark is 30×30 in wide mode (both expanded and collapsed). On narrow it's the
  // 40px drawer toggle, or the larger 57px sticky menu button on the home page
  // (Figma 8265-86809) — the menu-09 glyph scales with the mark size. `elevated`
  // (the chat overlay covering the screen) wins over both: Figma "Mobile Web
  // Default" (14701:127776) draws this hamburger at 42px, and 42 is exactly what
  // centres it on the SAME row as the chat header's own 3-dot trigger (top:24 +
  // 42/2 = 45, matching the trigger's 30px box sitting at top:30) — the home
  // page's oversized 57px button otherwise rode 7.5px low against it.
  const markSize = narrow ? (elevated ? 42 : largeMark ? 48 : 40) : 30;
  const [hover, setHover] = useState(false);

  // Dev menu: long-press (or right-click) the brand mark → the dev-only actions (reset animations,
  // mock data). Lives in ./dev/DevMenu so this header stays about the header.
  //
  // WHO GETS IT: a dev build always, and on a deployed origin only a caller inside the Wix corporate
  // network — designers and PMs QA the live app, so the long-press has to work on symphony.wix.com
  // for them and do NOTHING for a real user. Only the server can answer "is the VPN on" (from the
  // caller's IP), so the gesture is armed for everyone but opens nothing without that grant.
  //
  // Two things the gate is careful about, both regressions we've already had here:
  //   - The verdict is asked for on the long-press FIRING, never on mount or on an ordinary click,
  //     so a real user's page makes no extra request.
  //   - `onContextMenu` is bound only once unlocked. Binding it unconditionally is what made
  //     right-clicking the logo on symphony.wix.com swallow the browser's own context menu.
  const [devUnlocked, setDevUnlocked] = useState(devToolsAvailable);
  const [devMenuOpen, setDevMenuOpen] = useState(false);
  const longPressRef = useRef(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const startLongPress = () => {
    longPressRef.current = false;
    longPressTimer.current = setTimeout(() => {
      // A half-second hold is not a click: swallow the release either way, so a press with no grant
      // does nothing at all rather than toggling the sidebar.
      longPressRef.current = true;
      void requestDevToolsAccess().then((allowed) => {
        if (!allowed) return;
        setDevUnlocked(true);
        setDevMenuOpen(true);
      });
    }, DEV_LONG_PRESS_MS);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  };

  // A tab that already earned access keeps it across the reload the mock panel triggers — but the
  // cached grant is re-checked here, so it dies with the VPN rather than living on in the tab. No
  // request for a browser holding no grant, which is every real user's.
  useEffect(() => {
    if (devToolsAvailable || !devToolsUnlocked()) return;
    setDevUnlocked(true);
    void revalidateDevToolsAccess().then(setDevUnlocked);
  }, []);

  const ariaLabel = narrow
    ? sidebarOpen
      ? t('sidebar.closeConversations')
      : t('sidebar.openConversations')
    : collapsed
      ? t('sidebar.expand')
      : t('sidebar.collapse');
  const basePath = useBasePath();

  const railCurve = '420ms cubic-bezier(0.32, 0.72, 0, 1)';

  // The open mobile drawer carries its OWN brand row (mark + wordmark, which closes
  // it) per the Figma design, so this floating header would double it. Wide keeps
  // the header at every state; narrow shows it only as the closed drawer's toggle.
  if (narrow && sidebarOpen) return null;
  // A narrow voice call is full-screen and carries its OWN header (VoiceCallOverlay's top bar —
  // the collapse chevron + settings). This floating hamburger sits in the same top-start corner and
  // would cover that chevron, so it steps aside for the duration of the call.
  if (narrow && voiceCallSurfaceVisible) return null;

  const onBackClick = () => {
    logPageClickBi({ page_name: backPageName ?? 'menu', button_name: 'back', origin: 'header' });
    onBack?.();
  };

  // Drill-in screen on NARROW: the leading slot is BACK — the shared `BackButton` puck, the same
  // control every other drill-in screen (and the Agent skills sheet) uses. `showBack` is only ever
  // set on narrow (see AppFrame), so this always renders the puck; the wrapper just floats it in the
  // top-start corner where the hamburger otherwise sits. The dev menu lives on the brand link and is
  // absent in back mode, which is fine: it stays reachable on home and every MENU screen.
  if (showBack) {
    return (
      <div
        style={{
          position: 'absolute',
          // On narrow, drop below whichever top band is showing (each reserves its own space above
          // every screen's header, including the back button) — 0px when none is. The two are never up
          // together: the Black Friday band suppresses the welcome offer. Wide's bands live in the
          // content column, off to the side of this button, so it never needs this.
          top: narrow
            ? 'calc(24px + var(--black-friday-banner-h, 0px) + var(--personal-sale-banner-h, 0px))'
            : 24,
          insetInlineStart: 20,
          zIndex: elevated ? 70 : 7,
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <BackButton onClick={onBackClick} />
      </div>
    );
  }

  return (
    <>
    {/* Outer row — positions the whole header strip absolutely inside the sidebar.
        Structured as a flex row so the logo/wordmark link sits on the left and the
        action icon buttons (search, collapse) sit on the right, each independently
        clickable without stealing each other's events. */}
    <div
      style={{
        position: 'absolute',
        // On narrow, drop below whichever top band is showing (see the showBack branch above for the
        // same rule). Wide's bands live in the content column, off to the side of the rail this row
        // occupies, so it never needs this.
        top: narrow
          ? 'calc(24px + var(--black-friday-banner-h, 0px) + var(--personal-sale-banner-h, 0px))'
          : 24,
        // Narrow lines the menu button's left edge up with the 20px content
        // gutter (.orion-content-pane) so it sits under the greeting/date column;
        // wide keeps 11px to align the mark with the collapsed nav-rail column.
        insetInlineStart: narrow ? 20 : 11,
        zIndex: elevated ? 70 : 7,
        // Narrow matches the mark's own size (40, or 57 on home) so the row
        // doesn't clip the hamburger's circular backdrop. Wide (36) is sized to
        // the 36px action icon buttons so they aren't clipped vertically — the
        // 30px mark sits centered within it.
        height: narrow ? markSize : 36,
        boxSizing: 'border-box',
        // Collapsed takes the SAME 36px column as the collapsed nav items (NavRail's
        // `width: 36`, both inset by the content column's 11px), so the mark lands on
        // their shared centre. Sizing it to the mark itself instead left the row too
        // narrow for the brand button's inline padding to fit, which pulled the mark
        // off that centre and left it flush against its own clip edge.
        width: narrow ? undefined : collapsed ? 36 : 'calc(var(--sidebar-w, 260px) - 20px)',
        display: 'flex',
        alignItems: 'center',
        // The trailing action icons collapse to maxWidth 0; their gap would still eat
        // into the 36px column and shift the mark, so it goes with them.
        gap: collapsed && !narrow ? 0 : 2,
        // Only wide needs this, to clip the wordmark mid collapse/expand
        // transition. Narrow's width is fixed at exactly the mark's own size
        // with nothing else in flow to hide, so clipping here only risks
        // cutting off the unread-notification badge riding the mark's corner.
        overflow: narrow ? 'visible' : 'hidden',
        transition: `width ${railCurve}`,
      }}
    >
      {/* Logo + wordmark — the collapse/expand toggle on wide, the drawer toggle on narrow */}
      <Link
        href="/"
        as={`${basePath}/`}
        className="orion-brand-button"
        onClick={(e) => {
          if (longPressRef.current) {
            longPressRef.current = false;
            e.preventDefault();
            return;
          }
          e.preventDefault();
          if (narrow) {
            if (!sidebarOpen) {
              logPageClickBi({ page_name: 'chat_home', page_version: 'v3_chat_home', button_name: 'open_menu', location: 'header' });
            } else {
              logPageClickBi({ page_name: 'menu', button_name: 'close_menu', origin: 'menu' });
            }
            onToggle();
          } else {
            onToggleCollapsed?.();
          }
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => { setHover(false); cancelLongPress(); }}
        onPointerDown={startLongPress}
        onPointerUp={cancelLongPress}
        onPointerCancel={cancelLongPress}
        onContextMenu={devUnlocked ? (e) => { e.preventDefault(); setDevMenuOpen(true); } : undefined}
        aria-label={ariaLabel}
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          // On narrow the wordmark is hidden (maxWidth 0), so this inline value must
          // drop to 0 there too — otherwise it always wins over the CSS narrow-mode
          // `gap: 0` override (inline beats stylesheet), the content reads wider than
          // the CSS's fixed 38px narrow width, and centering the overflow clips the
          // mark's circular backdrop on its start (left) edge.
          gap: narrow || collapsed ? 0 : 8,
          height: '100%',
          // Collapsed centres the mark in the 36px column, so it must carry NO
          // horizontal padding — `.orion-brand-button`'s 6px/14px is sized for the
          // expanded row and does not fit here (inline wins over the stylesheet).
          justifyContent: collapsed && !narrow ? 'center' : undefined,
          paddingInline: collapsed && !narrow ? 0 : undefined,
          paddingInlineStart: narrow ? undefined : 0,
          textDecoration: 'none',
          borderRadius: 'var(--sym-radius-button)',
          border: 'none',
          // The collapsed hover highlight lives on the 30×30 mark box below, not here.
          backgroundColor: 'transparent',
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          color: 'var(--ds-text-primary)',
          cursor: 'pointer',
          // See the row's overflow comment above — same reasoning.
          overflow: narrow ? 'visible' : 'hidden',
          transition: `background-color 220ms ease`,
        }}
      >
        <div style={{ position: 'relative', width: markSize, height: markSize, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--ds-radius-8)', background: !narrow && collapsed && hover ? 'var(--ds-surface-standard-bg)' : 'transparent', transition: 'background-color 220ms ease', ...(narrow && showHamburger ? { borderRadius: '50%', background: 'var(--sym-bg-elevated)', boxShadow: '0 1px 6px rgba(var(--ds-black-rgb), 0.1)' } : {}) }}>
          {showHamburger
            ? <HamburgerMark size={markSize} />
            : (collapsed && hover)
              ? <PanelLeftOpen size={16} aria-hidden style={{ color: 'var(--ds-icon-primary)' }} />
              : <SymphonyMark size={markSize} />
          }
          {showHamburger && unreadCount > 0 && (
            <span
              aria-hidden
              style={{
                position: 'absolute',
                top: narrow ? 1 : 3,
                insetInlineEnd: narrow ? 1 : 3,
                width: narrow ? 11 : 8,
                height: narrow ? 11 : 8,
                borderRadius: '50%',
                background: 'var(--sym-peach)',
              }}
            />
          )}
        </div>
        <span
          className="orion-wordmark"
          style={{
            display: 'inline-flex',
            alignItems: 'baseline',
            fontFamily: 'var(--brand-font-display)',
            fontSize: 20,
            fontWeight: 800,
            letterSpacing: '-0.04em',
            color: 'var(--ds-text-primary)',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            maxWidth: collapsed || narrow ? 0 : 200,
            // No negative pull-back. A margin here changes what the row measures as its
            // content width, so under justify-content:center it moves the MARK rather
            // than the (zero-width) wordmark — off the icon column and into its own clip
            // edge. The gap it used to cancel is now zeroed outright when collapsed.
            marginInlineStart: 0,
            pointerEvents: collapsed ? 'none' : undefined,
            transition: `max-width ${railCurve}`,
          }}
        >
          Symphony
        </span>
      </Link>

      {/* Right-side action icons — search + collapse. Clip away with the rail when collapsed. */}
      {!narrow && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2,
            flexShrink: 0,
            overflow: 'hidden',
            // Two medium (36px) icon buttons + the 2px gap = 74px. The old 64
            // cap clipped the trailing collapse button under overflow:hidden.
            maxWidth: collapsed ? 0 : 74,
            transition: `max-width ${railCurve}`,
          }}
        >
          {onOpenSearch && (
            <IconButton
              aria-label={t('nav.search')}
              title={t('nav.search')}
              size="medium"
              variant="text"
              onClick={onOpenSearch}
              style={{ flexShrink: 0, color: 'var(--ds-icon-primary)' }}
            >
              <Search size={16} aria-hidden />
            </IconButton>
          )}
          <IconButton
            aria-label={t('sidebar.collapse')}
            title={t('sidebar.collapse')}
            size="medium"
            variant="text"
            onClick={onToggleCollapsed}
            style={{ flexShrink: 0, color: 'var(--ds-icon-primary)' }}
          >
            <PanelLeftClose size={16} aria-hidden />
          </IconButton>
        </span>
      )}
    </div>
    {devUnlocked && <DevMenu open={devMenuOpen} onClose={() => setDevMenuOpen(false)} zIndex={(elevated ? 70 : 7) + 1} />}
    </>
  );
}

// Two-line "=" hamburger — matches the native apps' menu-09 glyph (two rounded
// bars), not lucide's default three-line Menu icon. Geometry from Figma
// (Symphony menu-09, node 6320-12589): a 20px icon centred in the 40px button,
// each bar 13.33 long × 1.72 thick with 5.83 between centres (→ 4.11 edge gap).
// Expressed as ratios of the button size so it scales with `size`.
function HamburgerMark({ size = MARK_SIZE }: { size?: number }) {
  const barStyle: React.CSSProperties = {
    width: size * (13.333 / 40),
    height: size * (1.724 / 40),
    borderRadius: 999,
    background: 'var(--ds-icon-primary)',
  };
  return (
    <div
      aria-hidden
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: size * (4.109 / 40),
        width: size,
        height: size,
      }}
    >
      <span style={barStyle} />
      <span style={barStyle} />
    </div>
  );
}
